import React from 'react';
import { FilterSideBar } from '@ole-ui/ole-ui-components';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import useMobxStores from 'hooks/useMobxStores';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import { Text, TextVariants, Checkbox, Skeleton } from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import RangeFilters from './RangeFilters';

import './styles.scss';

type Props = {
  title?: string;
  /**
   *  Callback when a catalog filter is selected. Does not apply to the range filters.
   */
  onFilterSelect?: (groupKey: string, option: string) => void;
  /**
   * Whether to show the catalog filters like categories, products, etc. Defaults to false.
   */
  showOfferingFilters?: boolean;
  /**
   * Whether to show the full seats filter. Defaults to false.
   */
  showSeatsFilter?: boolean;
};

const LiveClassFilters = ({
  title,
  onFilterSelect,
  showOfferingFilters = true,
  showSeatsFilter = false,
}: Props) => {
  const { t } = useTranslation();
  const { liveClassOfferingFilterStore } = useMobxStores();
  const filterConfigs = liveClassOfferingFilterStore.getFilterConfigs();
  const filtersLoading = liveClassOfferingFilterStore.filterInfoLoading;

  const filterGroups = React.useMemo(() => {
    if (!showOfferingFilters) {
      return [];
    }
    return filterConfigs.map((filterConfig) => ({
      title: filterConfig.title,
      groupKey: filterConfig.key,
      'data-analytics-id': `${filterConfig.key}-filt-liveSess-lp`,
      options:
        liveClassOfferingFilterStore.filters[filterConfig.key]?.map(
          (option) => {
            const optionAnalyticsId = `${option.key}-${filterConfig.key}-filt-liveSess-lp`;

            return {
              ...option,
              title: option.value,
              navItemProps: {
                'data-analytics-id': `container-${optionAnalyticsId}`,
              },
              checkboxProps: {
                'data-analytics-id': optionAnalyticsId,
              },
            };
          },
        ) || [],
      mergeIdenticalLabels: filterConfig.groupIdenticalLabels,
    }));
  }, [
    filterConfigs,
    liveClassOfferingFilterStore.filters,
    showOfferingFilters,
  ]);

  const handleFilterSelect = (groupKey: string, option: string) => {
    liveClassOfferingFilterStore.toggleFilterOption(groupKey, option);
    onFilterSelect?.(groupKey, option);
  };

  return (
    <FilterSideBar
      expandedGroups={liveClassOfferingFilterStore.expandedFilterGroups}
      onGroupExpand={liveClassOfferingFilterStore.toggleFilterGroups}
      onFilterSelect={handleFilterSelect}
      aria-label={t('Filter the list of live class')}
    >
      <div className="live-class-filter__header">
        <Text className="live-class-filter__title" component={TextVariants.h3}>
          {title || t('Live class')}
        </Text>
      </div>
      {showSeatsFilter && (
        <div>
          <div className="live-class-filter__full-seats-filter pf-v5-theme-dark">
            <Checkbox
              data-analytics-id="hide-full-chkbox-offer-lvp"
              id="hide-full-chkbox-offer-lvp"
              name="full-live-class-filter"
              className="live-class-filter__full-checkbox"
              label={t('Hide full live class')}
              isChecked={liveClassOfferingFilterStore.hideFullLiveClass}
              isDisabled={filtersLoading}
              onChange={(event, checked) => {
                liveClassOfferingFilterStore.hideFullLiveClass = checked;
                liveClassOfferingFilterStore.filterEntries();
              }}
            />
          </div>
          <div className="live-class-filter__guaranteed-filter pf-v5-theme-dark">
            <Checkbox
              data-analytics-id="show-guaranteed-chkbox-offer-lvp"
              id="show-guaranteed-chkbox-offer-lvp"
              name="guaranteed-live-class-filter"
              className="live-class-filter__guaranteed-checkbox"
              label={t('Guaranteed to run')}
              isChecked={liveClassOfferingFilterStore.guaranteedRunLiveClass}
              isDisabled={filtersLoading}
              onChange={(event, checked) => {
                liveClassOfferingFilterStore.guaranteedRunLiveClass = checked;
                liveClassOfferingFilterStore.filterEntries();
              }}
            />
          </div>
        </div>
      )}
      {showOfferingFilters &&
        (filtersLoading ? (
          <div className="live-class-filter__skeleton" aria-busy>
            {filterConfigs.map((config) => (
              <div
                key={config.key}
                className="live-class-filter__skeleton-group"
              >
                <Skeleton
                  width="60%"
                  height="1.25rem"
                  screenreaderText={t('Loading')}
                  className="live-class-filter__skeleton-title"
                />
                {['opt-a', 'opt-b', 'opt-c'].map((optKey, i) => (
                  <Skeleton
                    key={`${config.key}-${optKey}`}
                    width={`${70 + (i % 3) * 10}%`}
                    height="1rem"
                    screenreaderText={t('Loading')}
                    className="live-class-filter__skeleton-option"
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <FilterSideBar.NavGroup>
            <FilterSideBar.ActiveFilterLabels
              groups={filterGroups}
              numLabels={5}
            />
            {filterGroups.map((fg) => (
              <FilterSideBar.FilterGroup key={fg.groupKey} {...fg} />
            ))}
          </FilterSideBar.NavGroup>
        ))}
      <RangeFilters />
    </FilterSideBar>
  );
};

export default withErrorBoundary(
  observer(LiveClassFilters),
  ComponentError,
  handleError,
);
